import type { AnyAction, Store } from 'redux';
import { matchQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';

import { queryClient } from '@/services/query/queryClient';

export interface QueryReduxMirror {
  /** The query key whose answer Redux holds now; null when it holds none. */
  mirroredQueryKey: (state: unknown) => QueryKey | null;
  actionType: string;
  /**
   * Required: Redux slots hold Immutable structures, and a plain answer breaks their readers' `.get` at run time.
   * `data` is untyped: the cache holds every key's data and cannot know the type of this one.
   */
  toPayload: (data: any) => unknown;
}

/** Writes each successful answer of a mirrored key into Redux before any component sees the query succeed. */
export const mirrorQueriesToRedux = (
  store: Pick<Store<unknown, AnyAction>, 'getState' | 'dispatch'>,
  mirrors: QueryReduxMirror[],
) =>
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success') return;
    const state = store.getState();
    mirrors
      .filter((mirror) => {
        const queryKey = mirror.mirroredQueryKey(state);
        return queryKey !== null && matchQuery({ queryKey, exact: true }, event.query);
      })
      .forEach((mirror) => store.dispatch({ type: mirror.actionType, payload: mirror.toPayload(event.query.state.data) }));
  });
